import { Subject } from 'rxjs';

/**
 * Base class for a task worker.
 * A task worker is a web worker that performs a task and emits the result when the task completes.
 * If the task worker is no longer needed, it should be destroyed to free up resources.
 * If the task worker is instructed to run while it is already running, the previous task is terminated, and the new task is run.
 * @template R The type of the result emitted when the task completes.
 */
export class TaskWorkerBase<R extends { type: 'complete' }> {
  /** Emits the result when the task completes. */
  public complete$: Subject<R> = new Subject<R>();

  /** Web Worker for the task. */
  private worker: Worker;

  /**
   * Creates a new task worker.
   * @param name The name of the task worker. Used in logging.
   * @param createWorker A function that creates a new web worker for the task.
   */
  constructor(private name: string, private createWorker: () => Worker) { }

  /**
   * Terminates the web worker and completes the complete$ observable.
   * The task worker can no longer be used after this method is called.
   */
  public destroy() {
    this.worker?.terminate();
    this.worker = null;
    this.complete$.complete();
  }

  /**
   * Runs the web worker with the provided message.
   * @param message The message to send to the web worker.
   */
  public run<T>(message: T) {
    this.worker?.terminate();
    this.worker = this.createWorker();

    this.worker.onmessage = ({ data }) => {
      if (data.type === 'complete') {
        this.complete$.next(data);
      }
    };

    this.worker.onerror = (error) => {
      console.error(`[${this.name}] worker error`, error);
    };

    this.worker.postMessage(message);
  }

  /** Terminates the web worker if it is running. */
  public stop() {
    this.worker?.terminate();
    this.worker = null;
  }
}
